<script setup>
    import LinkElement from "./LinkElement.vue";
    import {useCookie} from "@straylightagency/cookies";
    import {useLocalStorage} from "@straylightagency/storage";
    import {globalEmitter} from "@straylightagency/events";
    import {onMounted, ref} from "vue";
    import {__} from '../lib/localization';

    const props = defineProps({
        privacyLink: String,
    });

    const translateY = ref( 48 );

    const [, setSettings] = useCookie('cookies_settings', 'declined');
    const [showNotice, setShowNotice] = useLocalStorage('cookies.notice', null );

    async function declineCookies() {
        setShowNotice('set');
        setSettings( 'declined', {secure: location.protocol === 'https:' } );

        translateY.value = 34;
    }

    async function acceptCookies() {
        setShowNotice('set');
        setSettings( 'accepted', {secure: location.protocol === 'https:' } );

        translateY.value = 34;
    }

    onMounted( async () => {
        globalEmitter.on('showCookiesNotice', async () => {
            translateY.value = 0;
        } );

        if ( ! showNotice.value ) {
            translateY.value = 0;
        }
    } );
</script>

<template>
    <div class="cookies-notice | fixed bottom-2 right-0 left-0 z-50 translate-y-56 transition-transform duration-500" :style="{
        '--tw-translate-y': translateY + 'rem'
    }">
        <div class="mx-5 md:mx-20 flex justify-center">
            <div class="flex flex-col bg-dark-gray/50 border border-cosmic-gray p-6 w-full lg:w-7/9 xl:w-5/9 backdrop-blur-2xl" >
                <div class="flex flex-row mb-6">
                    <div class="flex-none w-auto mr-4">
                        <svg class="fill-transparent stroke-white stroke-[10px] w-16" xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 480.03 480.04">
                            <path d="M241.52,11.62c-.8-5.4-4.9-9.8-10.3-10.6-22.1-3.1-44.6.9-64.4,11.4l-74,39.5c-19.7,10.5-35.6,27-45.4,47.1L10.72,174.62C.92,194.72-2.28,217.52,1.62,239.52l14.5,82.8c3.9,22.1,14.6,42.3,30.7,57.9l60.3,58.4c16.1,15.6,36.6,25.6,58.7,28.7l83,11.7c22.1,3.1,44.6-.9,64.4-11.4l74-39.5c19.7-10.5,35.6-27,45.4-47.2l36.7-75.5c9.8-20.1,13-42.9,9.1-64.9-.9-5.3-5.3-9.3-10.6-10.1-51.5-8.2-92.8-47.1-104.5-97.4-1.8-7.6-8-13.4-15.7-14.6-54.6-8.7-97.7-52-106.2-106.8h.1,0ZM192.02,128.02c17.7,0,32,14.3,32,32s-14.3,32-32,32-32-14.3-32-32,14.3-32,32-32ZM128.02,320.02c0-17.7,14.3-32,32-32s32,14.3,32,32-14.3,32-32,32-32-14.3-32-32ZM352.02,256.02c17.7,0,32,14.3,32,32s-14.3,32-32,32-32-14.3-32-32,14.3-32,32-32Z"/>
                        </svg>
                    </div>

                    <div class="flex-none shrink">
                        <p class="text-white uppercase font-normal relative block mb-5
                            after:content-[''] after:w-20 after:h-0.5 after:bg-white after:absolute after:left-0 after:-bottom-2"
                        >{{ __('cookies.title') }}</p>

                        <p class="text-white/75 text-sm">
                            {{ __('cookies.informative') }}
                        </p>
                    </div>
                </div>

                <div class="flex flex-col md:flex-row gap-2">
                    <div class="w-full md:w-auto">
                        <link-element :href="props.privacyLink" class="button | w-full md:w-auto">
                            {{ __('cookies.more') }}
                            <span class="corner-top-left corner-top-right"></span>
                            <span class="corner-bottom-left corner-bottom-right"></span>
                        </link-element>
                    </div>
                    <button type="button" class="button | flex-none md:ml-auto" @click="declineCookies" :title="__('cookies.decline')">
                        <i class="fa-fw fa-solid fa-xmark"></i> {{ __('cookies.decline') }}
                        <span class="corner-top-left corner-top-right"></span>
                        <span class="corner-bottom-left corner-bottom-right"></span>
                    </button>
                    <button type="button" class="button important | flex-none" @click="acceptCookies" :title="__('cookies.accept')">
                        <i class="fa-fw fa-solid fa-check"></i> {{ __('cookies.accept') }}
                        <span class="corner-top-left corner-top-right"></span>
                        <span class="corner-bottom-left corner-bottom-right"></span>
                    </button>
                </div>

                <span class="corner-top-left corner-top-right"></span>
                <span class="corner-bottom-left corner-bottom-right"></span>
            </div>
        </div>
    </div>
</template>

<style scoped>
</style>